import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { KvRow, Panel, StatusBadge, btnSecondary, inputCls, money, shortDate, DeleteButton } from "@/components/admin/ui";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(null);
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get(`/customers/${id}`).then((r) => setData(r.data))
      .catch(() => { toast.error("Client introuvable"); navigate("/admin/app/customers"); });
    adminApi.get(`/customers/${id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs({ quotes: [], invoices: [] }));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="h-96 animate-pulse rounded-md border border-slate/15 bg-white" />;
  const c = data.customer;
  const m = data.metrics;

  const saveNote = async () => {
    if (!note.trim() || busy) return;
    setBusy(true);
    try {
      await adminApi.patch(`/customers/${c.id}`, { internal_note: note });
      setNote("");
      toast.success("Note ajoutée");
      load();
    } catch (error) {
      toast.error(apiError(error, "Erreur"));
    } finally { setBusy(false); }
  };

  const addTag = async () => {
    if (!tag.trim() || busy) return;
    setBusy(true);
    try {
      await adminApi.patch(`/customers/${c.id}`, { tags: [...(c.tags || []), tag.trim()] });
      setTag("");
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-customer-detail">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate(-1)} className={btnSecondary} data-testid="customer-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink" data-testid="customer-name">{c.first_name} {c.last_name}</h1>
          <p className="mt-0.5 text-sm text-slate">Client depuis le {shortDate(c.created_at)} · source {c.source || "web"}</p>
        </div>
        <div className="ml-auto">
          <DeleteButton testId="customer-delete" label="Supprimer ce client"
            onConfirm={() => adminApi.delete(`/customers/${id}`)}
            onDeleted={() => { toast.success("Client supprimé"); navigate("/admin/app/customers"); }} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-4">
          <Panel title="Identité" testId="customer-identity">
            <KvRow label="E-mail">{c.email ? <a href={`mailto:${c.email}`} className="text-miles hover:underline">{c.email}</a> : "—"}</KvRow>
            <KvRow label="Téléphone">{c.phone ? <a href={`tel:${c.phone}`} className="font-mono text-miles hover:underline">{c.phone}</a> : "—"}</KvRow>
            <KvRow label="Langue">{c.lang || "fr"}</KvRow>
            <KvRow label="Dernière réservation">{shortDate(m.last_booking)}</KvRow>
          </Panel>

          <Panel title="Métriques" testId="customer-metrics">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded border border-slate/10 bg-slate/10">
              {[["Courses", m.bookings_count], ["À venir", m.upcoming], ["CA total", money(m.revenue_total)],
                ["Panier moyen", money(m.avg_basket)], ["Annulations", m.cancellations], ["Remboursements", m.refunds]].map(([label, value]) => (
                <div key={label} className="bg-white p-3">
                  <p className="text-xs text-slate">{label}</p>
                  <p className="mt-0.5 font-mono text-lg font-semibold text-ink" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{value}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Tags & notes internes" testId="customer-crm">
            <div className="flex flex-wrap gap-1.5">
              {(c.tags || []).map((t) => (
                <span key={t} className="rounded bg-miles-ice px-2 py-1 text-xs font-semibold text-miles">{t}</span>
              ))}
              {(c.tags || []).length === 0 && <span className="text-xs text-slate">Aucun tag</span>}
            </div>
            <div className="mt-2 flex gap-2">
              <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="vip, hôtel, presse…"
                data-testid="tag-input" className={inputCls} />
              <button type="button" onClick={addTag} disabled={!tag.trim()} className={btnSecondary} data-testid="add-tag">
                <Plus className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="mt-3 flex gap-2 border-t border-slate/10 pt-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (jamais visible client)…"
                data-testid="customer-note-input" className={inputCls} />
              <button type="button" onClick={saveNote} disabled={!note.trim() || busy} className={btnSecondary} data-testid="add-customer-note">
                <Plus className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            {(c.internal_notes || []).map((n, i) => (
              <p key={i} className="mt-2 rounded bg-cloud px-3 py-2 text-sm text-ink">
                {n.text} <span className="block font-mono text-[11px] text-slate">{n.at?.slice(0, 16).replace("T", " ")} · {n.by}</span>
              </p>
            ))}
          </Panel>
        </div>

        <div className="lg:col-span-8">
          <Panel title={`Historique (${data.bookings.length})`} testId="customer-history">
            {data.bookings.length === 0 && <p className="text-sm text-slate">Aucune réservation.</p>}
            <div className="divide-y divide-slate/10">
              {data.bookings.map((b) => (
                <Link key={b.id} to={`/admin/app/bookings/${b.id}`} data-testid={`history-${b.tracking_code}`}
                  className="flex items-center justify-between gap-3 py-2.5 text-sm transition-colors hover:bg-miles-ice/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-mono font-semibold text-miles">{b.tracking_code}</span>
                    <span className="hidden font-mono text-xs text-slate sm:inline">{b.date} {b.time}</span>
                    <span className="truncate text-ink">{b.pickup} → {b.dropoff || "à l'heure"}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={b.display_status} />
                    <span className="font-mono text-sm font-medium text-ink">{money(b.price_chf)}</span>
                  </span>
                </Link>
              ))}
            </div>
          </Panel>

          {docs && (docs.invoices.length > 0 || docs.quotes.length > 0) && (
            <Panel title="Documents" testId="customer-documents" action={
              <span className="text-xs font-normal text-slate">
                Facturé {money(docs.invoiced_total)} · Solde ouvert {money(docs.open_balance)}
              </span>}>
              <div className="space-y-4">
                {docs.invoices.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate/60">Factures</p>
                    <div className="divide-y divide-slate/10">
                      {docs.invoices.map((inv) => (
                        <Link key={inv.id} to={`/admin/app/invoices/${inv.id}`} data-testid={`doc-invoice-${inv.number || inv.id}`}
                          className="flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:bg-miles-ice/30">
                          <span className="font-mono font-semibold text-miles">{inv.number || "Brouillon"}</span>
                          <span className="hidden text-xs text-slate sm:inline">{shortDate(inv.issued_at)}</span>
                          <span className="flex shrink-0 items-center gap-2">
                            <StatusBadge status={inv.status} />
                            <span className="font-mono text-sm font-medium text-ink">{money(inv.balance > 0 ? inv.balance : inv.total)}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {docs.quotes.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate/60">Devis</p>
                    <div className="divide-y divide-slate/10">
                      {docs.quotes.map((q) => (
                        <Link key={q.id} to={`/admin/app/quotes/${q.id}`} data-testid={`doc-quote-${q.number}`}
                          className="flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:bg-miles-ice/30">
                          <span className="font-mono font-semibold text-miles">{q.number}</span>
                          <span className="hidden text-xs text-slate sm:inline">{shortDate(q.created_at)}</span>
                          <span className="flex shrink-0 items-center gap-2">
                            <StatusBadge status={q.status} />
                            <span className="font-mono text-sm font-medium text-ink">{money(q.total)}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
